import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { ApiError } from '../../api/client'
import { useAuth } from '../../auth/useAuth'
import { useAdaptivePolling } from '../../hooks/useAdaptivePolling'
import { PartnerOffersBadgeContext } from './partnerOffersBadgeContextRef'
import { fetchIncomingOffersCount, fetchOrdersCount } from './services/partnersApi'

const POLL_INTERVAL_MS = 60000

/**
 * Licznik ofert partnerskich pod badge w menu (wzorzec MarketStatusContext).
 * Moduł partnerski jest za flagą backendu — 404 na liczniku oznacza "wyłączone":
 * przestajemy odpytywać i chowamy pozycje menu/akcje (do następnego logowania).
 */
export function PartnerOffersBadgeProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [count, setCount] = useState(0)
  const [ordersCount, setOrdersCount] = useState(0)
  const [ordersEnabled, setOrdersEnabled] = useState<boolean | null>(null)
  const [nonce, setNonce] = useState(0)
  const enabledRef = useRef<boolean | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const tick = useCallback(async () => {
    if (!token || enabledRef.current === false) {
      return
    }
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    try {
      const data = await fetchIncomingOffersCount(token, controller.signal)
      enabledRef.current = true
      setEnabled(true)
      setCount(typeof data?.count === 'number' ? data.count : 0)
      // Rev 8: badge zleceń (read-state — nowe NEW/CANCELLED od ostatniego wejścia);
      // 404 = orders-enabled off — chowamy tylko badge, pozycję chowa flaga modułu.
      try {
        const orders = await fetchOrdersCount(token, controller.signal)
        setOrdersEnabled(true)
        setOrdersCount((orders?.new ?? 0) + (orders?.cancelled ?? 0))
      } catch (ordersError) {
        if (controller.signal.aborted) {
          return
        }
        setOrdersCount(0)
        if (ordersError instanceof ApiError && ordersError.status === 404) {
          setOrdersEnabled(false) // stan przejściowy rolloutu: sharing on, orders off
        }
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return
      }
      if (error instanceof ApiError && error.status === 404) {
        enabledRef.current = false
        setEnabled(false)
        setCount(0)
        setOrdersCount(0)
        setOrdersEnabled(false)
      }
      // inne błędy (sieć/5xx): zostaw poprzedni stan, spróbujemy w kolejnym tiku
    }
  }, [token])

  useEffect(() => {
    enabledRef.current = null
    if (!token) {
      return undefined
    }
    // setTimeout(0) zamiast wywołania synchronicznego w ciele efektu — patrz wzorzec w
    // useSourcePublicationLinks.ts (react-hooks/set-state-in-effect).
    const initialTickTimer = window.setTimeout(() => {
      void tick()
    }, 0)
    return () => {
      window.clearTimeout(initialTickTimer)
      abortRef.current?.abort()
    }
  }, [tick, token, nonce])

  // Licznik w menu (2 endpointy co 60 s): pauza w ukrytej karcie, odświeżenie zaraz po powrocie.
  const getDelayMs = useCallback(() => POLL_INTERVAL_MS, [])
  useAdaptivePolling({ enabled: Boolean(token) && enabled !== false, getDelayMs, refresh: tick })

  const refresh = useCallback(() => setNonce((value) => value + 1), [])

  const value = useMemo(() => ({ enabled, count, ordersCount, ordersEnabled, refresh }), [enabled, count, ordersCount, ordersEnabled, refresh])

  return <PartnerOffersBadgeContext.Provider value={value}>{children}</PartnerOffersBadgeContext.Provider>
}
